import type { FC } from "react"
import { useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useConfigModel, useControllerModel, useStoreListModel, useInsightModel } from "@/models"
import { GeoJsonLayer, useMapGLContext } from "@wayz/react-gl"
import { scaleQuantile } from "d3-scale"
import { groupBy } from "lodash-es"
import { useCreation, useRequest, useSetState } from "ahooks"
import type { AreaCoutTreeItem, BranchFilters } from "@/api"
import { getAreaCount, getGeojsonByCode, getPosition, getUploadPoiAreaCount } from "@/api"
import { polygonHeatColors } from "@/constants/constant"
import { colorToRGBArray, getImageSize, getTreeItem, parseTreeJson } from "@/util/util"
import { getBranchThemeColors, getCurrentPageType, getCurrentAreaConfig } from "@/util/commonUtils"
import { createSVGIcon, svgToDataURL } from "../../createSvg.new"
import {
  provincesReplace,
  specialAreas,
  showZoomMap,
  offsetMap,
  TYPE_WAYZ_POI,
} from "@/constants/constant"
// ==============================================
import CityGeoJSONLayer from "@/components/CityGeoJSONLayer"
// ==============================================
export const cityStoreHeatPolygonLayer = "city-store-heat-polygon-layer"
const areaId = "areaCode"
// ==============================================
interface stateVO {
  geoFeatureCollection?: any
}
interface AreaHeatProps {
  // map visible
  visible: boolean
  // 沙盘id
  sandTableId: string
  // 城市id
  cityId: string
  // 品牌id
  brandId: string
  // 显示类型
  showType: string
  // 点击城市边界回调
  cityGeoJsonLayerCallback?: () => void
  // 点击区县边界回调
  districtGeoJsonLayerCallback?: () => void
}
const AreaHeat: FC<AreaHeatProps> = ({
  visible,
  sandTableId,
  cityId,
  brandId,
  showType,
  cityGeoJsonLayerCallback,
  districtGeoJsonLayerCallback,
}) => {
  const navigate = useNavigate()
  const { setViewState, setHoverInfo } = useMapGLContext()
  const { theme } = useControllerModel()
  const { industryCategory, configs } = useConfigModel()
  const { insightState } = useInsightModel()
  const { storeList, storeType, customRefreshCount } = useStoreListModel()
  const [state, setState] = useSetState<stateVO>({
    geoFeatureCollection: null,
  })
  const { geoFeatureCollection } = state
  const pageType = getCurrentPageType()

  // 获取网点主题颜色
  const colorTheme = useCreation(() => getBranchThemeColors(), [theme])

  // 获取当前区域范围
  const areaConfig = useCreation(() => {
    return getCurrentAreaConfig(cityId, areaId, configs)
  }, [cityId, configs])

  // 获取省市区网点热力
  const { data: areaCountData } = useRequest(
    (filterData?: BranchFilters) =>
      getAreaCount({
        ...filterData,
        brandId,
        hasAuth: true,
      }),
    {
      refreshDeps: [brandId],
    },
  )

  // 统计品牌地区自定义网点数量
  const { data: uploadCountData } = useRequest(
    () =>
      getUploadPoiAreaCount({
        sandTableId,
        brandId,
      }),
    {
      refreshDeps: [sandTableId, brandId, customRefreshCount],
    },
  )

  // 网点热力数据
  const countData = useCreation(() => {
    if (storeType === TYPE_WAYZ_POI) {
      return areaCountData || []
    } else {
      return uploadCountData || []
    }
  }, [uploadCountData, areaCountData, storeType])

  // 定位
  const locationTo = useCallback(
    (longitude, latitude, zoom) => {
      if (longitude && latitude) {
        setViewState({
          longitude,
          latitude,
          zoom,
        })
      }
    },
    [setViewState],
  )

  // 获取颜色函数
  const getColor = useCallback((countData) => {
    const counts =
      countData
        .map((item: AreaCoutTreeItem) => item.poiCount)
        .sort((a: number, b: number) => a - b) || []

    return scaleQuantile()
      .domain(counts)
      .range(polygonHeatColors as any) as unknown as any
  }, [])

  // 获取properties的color
  const getPropertiesColor = useCallback((count, color) => {
    return count > 0 ? colorToRGBArray(color(count)) : colorToRGBArray(polygonHeatColors[0])
  }, [])

  // 获取employeeCount
  const getEmployeeCount = useCallback((count) => {
    let employeeCount = null
    if (typeof count === "number") employeeCount = count

    return employeeCount
  }, [])

  // 给properties赋值
  const setPropertiesValues = useCallback((obj, props) => {
    for (const key in props) {
      obj[key] = props[key]
    }
  }, [])

  // 设置city geo
  const setCityGeoFeature = useCallback(
    (geo, level) => {
      const adcode = geo?.properties?.adcode
      const areaCodes = areaConfig?.children?.map((item: any) => item[areaId]) || []
      const keys = Object.keys(provincesReplace)
      // 如果areaCodes存在直辖市，则将对应省份code放入areaCodes中
      for (const key of keys) {
        const cityCode = provincesReplace[key]
        if (areaCodes.includes(cityCode)) {
          areaCodes.push(Number(key))
        }
      }
      const gf = Object.assign({}, geo)
      const cityCountData = parseTreeJson(countData).filter((item) => item.level === level)
      // 获取颜色函数
      const color = getColor(cityCountData)

      // 台湾不过滤数据
      if (adcode && !specialAreas.includes(adcode)) {
        gf.features = gf.features.filter(
          (feature: any) => areaCodes.indexOf(feature.properties.adcode) >= 0,
        )
      }

      gf.features.forEach((feature: any) => {
        const code = feature.properties.adcode
        const city = cityCountData.find((item) => item.id === code)
        const count = city?.poiCount || 0

        // 给properties赋值
        setPropertiesValues(feature.properties, {
          count,
          employeeCount: getEmployeeCount(city?.employeeCount),
          color: getPropertiesColor(count, color),
        })
      })

      setState({
        geoFeatureCollection: gf,
      })
    },
    [
      countData,
      setState,
      getColor,
      setPropertiesValues,
      getEmployeeCount,
      getPropertiesColor,
      areaConfig,
    ],
  )

  // 设置district geo
  const setDistrictGeoFeature = useCallback(
    (gf, districtCountData) => {
      const areaCodes = areaConfig?.children?.map((item: any) => item[areaId]) || []
      // 获取颜色函数
      const color = getColor(districtCountData)

      const store = storeList?.slice() ?? []
      const districtGroupMap = groupBy(store, (store) => store.districtCode)

      // 单个区县不做处理
      if (gf.features.length > 1) {
        gf.features = gf.features.filter(
          (feature: any) => areaCodes.indexOf(feature.properties.adcode) >= 0,
        )
      }

      gf.features.forEach((feature: any) => {
        const code = feature.properties.adcode
        const stores = districtGroupMap[code] ?? []
        const district = districtCountData.find((item: AreaCoutTreeItem) => item.id === code)
        const count = district?.poiCount || 0

        // 给properties赋值
        setPropertiesValues(feature.properties, {
          count,
          employeeCount: getEmployeeCount(district?.employeeCount),
          color: getPropertiesColor(count, color),
          stores,
        })
      })

      setState({
        geoFeatureCollection: gf,
      })
    },
    [
      getColor,
      storeList,
      setState,
      setPropertiesValues,
      getEmployeeCount,
      getPropertiesColor,
      areaConfig,
    ],
  )

  // 根据省市区code获取geojson数据
  const { run: geoRun } = useRequest(getGeojsonByCode, {
    manual: true,
    onSuccess: (geo: any) => {
      if (!geo) return
      const level = areaConfig?.level

      if (level === 0) {
        // 设置city geo
        setCityGeoFeature(geo, "PROVINCE")
      } else if (level === 1) {
        // 设置city geo
        setCityGeoFeature(geo, "CITY")
      } else if (level === 2) {
        const gf = Object.assign({}, geo)
        const code = areaConfig?.areaCode

        const districtCountData = getTreeItem(countData, code, "id")?.children || []

        // 设置district geo
        setDistrictGeoFeature(gf, districtCountData)
      } else if (level === 3) {
        const gf = Object.assign(
          {
            features: [geo],
          },
          geo,
        )
        const code = gf.properties.adcode

        const district = getTreeItem(countData, code, "id")
        const districtCountData = district ? [district] : []

        // 设置district geo
        setDistrictGeoFeature(gf, districtCountData)
      }
    },
  })

  // 清空城市区县热力图块
  const clearHeatCollection = useCallback(() => {
    setState({
      geoFeatureCollection: null,
    })
  }, [setState])

  // 区域范围
  useEffect(() => {
    if (!areaConfig) return
    const id = areaConfig[areaId]

    // 清空城市区县热力图块
    clearHeatCollection()

    // 省份、城市、区县
    geoRun(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaConfig?.[areaId], countData, storeList, clearHeatCollection, geoRun])

  // 定位
  useEffect(() => {
    if (!areaConfig) return
    const id = areaConfig[areaId]
    const level = areaConfig?.level

    // 根据code获取全国或省市区的经纬度
    getPosition(id).then((position) => {
      const { longitude, latitude } = position ?? {}
      // 定位
      if (longitude && latitude) {
        if (level === 0) {
          locationTo(longitude + offsetMap.CN, latitude, showZoomMap.CN)
        } else if (level === 1) {
          locationTo(longitude + offsetMap.PROVINCES, latitude, showZoomMap.PROVINCES)
        } else if (level === 2) {
          locationTo(longitude + offsetMap.CITY, latitude, showZoomMap.CITY)
        } else if (level === 3) {
          locationTo(longitude + offsetMap.DISTRICT, latitude, showZoomMap.DISTRICT)
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaConfig?.[areaId], locationTo])

  // 当点击省市区边界时
  const geoJsonLayerClick = useCallback(
    ({ object }) => {
      const { adcode, stores = [], center = [] } = object.properties
      const [lng, lat] = center
      const level = areaConfig?.level || 0

      // 当adcode是直辖市的省份code时，将其换成对应城市code
      const codes = Object.keys(provincesReplace).map((v) => Number(v))
      const code = codes.includes(adcode) ? provincesReplace[adcode] : adcode

      // 取消热力图hover提示
      setHoverInfo(null as any)

      // 点击有子项的图块时
      if (level < 3) {
        // 点击城市边界回调
        cityGeoJsonLayerCallback?.()
      } else {
        const store = stores?.[0] ?? {}
        const longitude = store.longitude || lng
        const latitude = store.latitude || lat
        // 定位
        if (longitude & latitude) {
          locationTo(longitude, latitude, showZoomMap.STORE)
        }
        // 点击区县边界回调
        districtGeoJsonLayerCallback?.()
      }

      // 跳转页面
      if (code && code !== Number(cityId)) {
        navigate(`/${sandTableId}/${brandId}/${code}/${pageType}`)
      }
    },
    [
      sandTableId,
      cityId,
      brandId,
      setHoverInfo,
      cityGeoJsonLayerCallback,
      navigate,
      pageType,
      locationTo,
      districtGeoJsonLayerCallback,
      areaConfig,
    ],
  )

  // hover
  const onHover = useCallback(
    async (info) => {
      const { object, x, y } = info
      if (object) {
        const { strokeColor, fillColor, textColor, textColor2 } = colorTheme
        const { name, count = 0, employeeCount } = object.properties
        const imgUrl = svgToDataURL(
          createSVGIcon(
            name,
            count,
            employeeCount,
            industryCategory,
            strokeColor,
            fillColor,
            textColor,
            textColor2,
          ),
        )
        const html = <img src={imgUrl} alt="" />

        const { width, height } = await getImageSize(imgUrl)
        const offset = {
          x: -width + 35,
          y: -height,
        }
        setHoverInfo(
          Object.assign({}, info, {
            x: x + offset.x,
            y: y + offset.y,
            html,
          }),
        )
      } else {
        setHoverInfo(null as any)
      }
    },
    [colorTheme, setHoverInfo, industryCategory],
  )

  if (!areaConfig) return null
  return areaConfig?.level === 3 ? (
    // 省市区边界
    <CityGeoJSONLayer id="district" zIndex={-1} />
  ) : (
    // 省市区模块
    <GeoJsonLayer
      id={cityStoreHeatPolygonLayer}
      visible={visible && showType !== "STORE"}
      pickable={!insightState.editing}
      data={geoFeatureCollection}
      stroked={true}
      getLineColor={colorToRGBArray("rgba(4, 13, 23, 1)")}
      getLineWidth={1}
      lineWidthUnits="pixels"
      getFillColor={(f) => f.properties.color}
      zIndex={2}
      onHover={onHover}
      onClick={geoJsonLayerClick}
      updateTriggers={{ onHover }}
      opacity={0.1}
    />
  )
}

export default AreaHeat
